/**
 * LinkedInProfilePage — /linkedin-presence/profile
 *
 * Profile optimization prompts (Headline, About, Skills, Featured) plus
 * the monthly Analytics health check prompt at the bottom.
 *
 * Headline rewriter is free; rest are All Access.
 */

import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LockIcon from "@mui/icons-material/Lock";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import {
  PROFILE_PROMPTS,
  ANALYTICS_PROMPTS,
  type LinkedInPrompt,
} from "../data/linkedinPresenceContent";
import { L } from "../utils/localized";
import { useAuth } from "../hooks/useAuth";
import { getTierAccess } from "../utils/tier";
import ProUpsellNote from "../components/ProUpsellNote";
import AnalyzerCard from "../components/linkedin/AnalyzerCard";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

function PromptCard({ prompt, canAccess, isPro }: { prompt: LinkedInPrompt; canAccess: boolean; isPro: boolean }) {
  const { t } = useTranslation("pages-products");
  const theme = useTheme();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(prompt.promptText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const unlocked = canAccess || prompt.tier === "free";

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: { xs: 3, md: 4 }, mb: 3 }} id={prompt.slug}>
      <Stack direction="row" spacing={2} alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
        <Box flex={1} minWidth={0}>
          <Stack direction="row" spacing={1} alignItems="center" mb={0.5} flexWrap="wrap" gap={0.5}>
            <Typography variant="h6" fontWeight={500}>{prompt.title}</Typography>
            {prompt.tier === "free" ? (
              <Chip label={t("LinkedInProfilePage.freeChipLabel")} size="small" sx={{ bgcolor: alpha(theme.palette.success.main, 0.12), color: theme.palette.success.dark, fontWeight: 500, fontSize: "0.68rem", height: 20 }} />
            ) : (
              <Chip icon={<LockIcon sx={{ fontSize: "0.8rem !important" }} />} label={t("LinkedInProfilePage.allAccessChipLabel")} size="small" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, fontWeight: 500, fontSize: "0.68rem", height: 20 }} />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", lineHeight: 1.5 }}>{prompt.tagline}</Typography>
        </Box>
      </Stack>

      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" gap={1}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <ScheduleIcon sx={{ fontSize: 14, color: "text.disabled" }} />
          <Typography variant="caption" color="text.secondary">{prompt.timeEstimate}</Typography>
        </Stack>
      </Stack>

      <Box mb={2.5}>
        {prompt.whatItDoes.map((para, i) => (
          <Typography key={i} variant="body2" sx={{ mb: 1.25, lineHeight: 1.75 }}>{para}</Typography>
        ))}
      </Box>

      <Paper elevation={0} sx={{ bgcolor: alpha(theme.palette.info.main, 0.04), border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`, borderRadius: 2, p: 2, mb: 2.5 }}>
        <Typography variant="caption" fontWeight={700} sx={{ color: theme.palette.info.dark, letterSpacing: "0.05em", textTransform: "uppercase", display: "block", mb: 1 }}>
          {t("LinkedInProfilePage.gatherBeforePromptLabel")}
        </Typography>
        <Stack spacing={0.5}>
          {prompt.inputsRequired.map((input, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
              <Typography variant="body2" color="text.disabled" sx={{ minWidth: 20 }}>{i + 1}.</Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.55 }}>{input}</Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>

      {unlocked ? (
        <Paper elevation={0} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`, borderRadius: 2, p: 2.5, mb: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="caption" fontWeight={700} sx={{ color: theme.palette.primary.main, letterSpacing: "0.05em", textTransform: "uppercase" }}>{t("LinkedInProfilePage.thePromptLabel")}</Typography>
            <Tooltip title={copied ? t("LinkedInProfilePage.copiedTooltip") : t("LinkedInProfilePage.copyToClipboardTooltip")}>
              <IconButton size="small" onClick={handleCopy} sx={{ color: copied ? theme.palette.success.dark : theme.palette.primary.main }}>
                {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
          <Box component="pre" sx={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.78rem", lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0, maxHeight: 500, overflowY: "auto", p: 1 }}>
            {prompt.promptText}
          </Box>
        </Paper>
      ) : isPro ? <ProUpsellNote /> : (
        <Paper elevation={0} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), border: `1px dashed ${alpha(theme.palette.primary.main, 0.35)}`, borderRadius: 2, p: 3, textAlign: "center", mb: 2.5 }}>
          <LockIcon sx={{ fontSize: 32, color: alpha(theme.palette.primary.main, 0.5), mb: 1.5 }} />
          <Typography variant="body1" fontWeight={500} gutterBottom>{t("LinkedInProfilePage.unlockWithAllAccessMessage")}</Typography>
          <Button variant="contained" onClick={() => navigate("/pricing")} endIcon={<ArrowForwardIcon />} sx={{ bgcolor: theme.palette.primary.main, fontWeight: 500, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) } }}>
            {t("LinkedInProfilePage.seeAllAccessPricingButton")}
          </Button>
        </Paper>
      )}

      <Paper elevation={0} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04), border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`, borderRadius: 2, p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <AutoAwesomeIcon sx={{ color: theme.palette.primary.main, fontSize: 18, mt: "2px", flexShrink: 0 }} />
          <Box>
            <Typography variant="caption" fontWeight={700} sx={{ color: theme.palette.primary.main, letterSpacing: "0.05em", textTransform: "uppercase", display: "block", mb: 0.5 }}>
              {t("LinkedInProfilePage.agenticAISharpeningLabel")}
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.65 }}>{prompt.agenticAIGuidance}</Typography>
          </Box>
        </Stack>
      </Paper>
    </Paper>
  );
}

export default function LinkedInProfilePage() {
  const { t, i18n } = useTranslation("pages-products");
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const tierAccess = getTierAccess(user);
  const canAccess = tierAccess.canAccessProducts;
  const profilePrompts = L(PROFILE_PROMPTS, i18n.language);
  const analyticsPrompts = L(ANALYTICS_PROMPTS, i18n.language);

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      <Box sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`, borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`, py: { xs: 4, md: 6 } }}>
        <Container maxWidth="md">
          <Stack direction="row" spacing={1} alignItems="center" mb={3}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/linkedin-presence")} size="small" sx={{ color: "text.secondary", textTransform: "none", fontWeight: 500 }}>{t("LinkedInProfilePage.breadcrumbLinkedInPresence")}</Button>
            <Typography variant="body2" color="text.disabled">›</Typography>
            <Typography variant="body2" color="text.secondary">{t("LinkedInProfilePage.breadcrumbProfileOptimization")}</Typography>
          </Stack>
          <Typography variant="h3" fontWeight={500} sx={{ letterSpacing: "-0.02em", mb: 1 }}>{t("LinkedInProfilePage.pageTitle")}</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720, lineHeight: 1.65 }}>
            {t("LinkedInProfilePage.pageIntro")}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        {/* AI Profile Analyzer — free lead magnet (D4). Paste text, get a scored
            five-dimension critique with rewrites. Works logged-out. */}
        <Typography variant="h5" fontWeight={500} sx={{ mb: 1, letterSpacing: "-0.02em" }}>
          {t("LinkedInProfilePage.analyzerHeading")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t("LinkedInProfilePage.analyzerSubheading")}
        </Typography>
        <AnalyzerCard />

        <Divider sx={{ my: 5 }} />

        {!canAccess && (
          tierAccess.isPro ? <ProUpsellNote /> : (
            <Alert severity="info" icon={<LockIcon />} sx={{ mb: 4, borderRadius: 2 }} action={<Button size="small" onClick={() => navigate("/pricing")} endIcon={<ArrowForwardIcon />}>{t("LinkedInProfilePage.seeAllAccessButton")}</Button>}>
              <Trans
                i18nKey="LinkedInProfilePage.headlineFreeNotice"
                t={t}
                components={[<strong key="strong0" />]}
              />
            </Alert>
          )
        )}

        {profilePrompts.map((p) => <PromptCard key={p.slug} prompt={p} canAccess={canAccess} isPro={tierAccess.isPro} />)}

        <Divider sx={{ my: 5 }} />

        <Typography variant="h5" fontWeight={500} sx={{ mb: 3, letterSpacing: "-0.02em" }}>{t("LinkedInProfilePage.analyticsIterationHeading")}</Typography>
        {analyticsPrompts.map((p) => <PromptCard key={p.slug} prompt={p} canAccess={canAccess} isPro={tierAccess.isPro} />)}

        <Paper elevation={0} sx={{ mt: 5, p: 4, textAlign: "center", bgcolor: alpha(theme.palette.primary.main, 0.04), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, borderRadius: 3 }}>
          <Typography variant="h5" fontWeight={500} gutterBottom>{t("LinkedInProfilePage.footerCtaTitle")}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 520, mx: "auto" }}>
            {t("LinkedInProfilePage.footerCtaBody")}
          </Typography>
          <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />} onClick={() => navigate("/linkedin-presence/content")} sx={{ bgcolor: theme.palette.primary.main, fontWeight: 500, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) } }}>
            {t("LinkedInProfilePage.seeContentStrategyButton")}
          </Button>
        </Paper>
      </Container>
    </Box>
  );
}
